import { useCallback, useRef } from 'react';
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { useGetUsers } from 'src/api/user';
import useSupplies from 'src/api/hooks/use-supplies';
import useProducts from 'src/api/hooks/use-products';
import useInvoices from 'src/api/hooks/use-invoices';
import useCustomers from 'src/api/hooks/use-customers';
import useCheckout from 'src/hooks/use-checkout';
import type {
  WsIncomingMessage,
  WsOutgoingMessage,
  WsAuthResponseMessage,
  WsUserEventMessage,
  WsTransactionCreatedMessage,
  WsTransactionUpdatedMessage,
  WsSessionUpdatedMessage,
  WsVoucherCreatedMessage,
  WsVoucherUpdatedMessage,
  WsReturnCreatedMessage,
  WsReturnUpdatedMessage,
  WsProductsFromErpMessage,
  WsCustomerCreatedMessage,
  WsNotificationCreatedMessage,
  WsInitDataDeferredMessage,
} from '../types';
import {
  isAuthResponseMessage,
  isUserEventMessage,
  isTransactionCreatedMessage,
  isTransactionUpdatedMessage,
  isSessionUpdatedMessage,
  isVoucherCreatedMessage,
  isVoucherUpdatedMessage,
  isReturnCreatedMessage,
  isReturnUpdatedMessage,
  isProductsFromErpMessage,
  isRolesUpdatedMessage,
  isSupplyCreatedMessage,
  isCustomerCreatedMessage,
  isNotificationCreatedMessage,
  isInitDataStartMessage,
  isInitDataChunkMessage,
  isInitDataCompleteMessage,
  isInitDataDeferredMessage,
} from '../types';

// ---------------------------------------------------------------------------
// Custom hook for managing WebSocket message handlers
// Separates business logic from connection management
// ---------------------------------------------------------------------------

interface UseWsHandlersOptions {
  onClientKeyChange?: (key: string | null) => void;
}

interface UseWsHandlersReturn {
  handleMessage: (message: WsIncomingMessage) => void;
  handleAuth: (user: any, key: string | null, configSelected?: string) => WsOutgoingMessage;
  handleLogout: (key: string | null) => WsOutgoingMessage;
}

// Accumulateurs pour le streaming des donnees d'initialisation
const initDataAccumulators = {
  products: [] as any[],
  users: [] as any[],
  customers: [] as any[],
  transactions: [] as any[],
};

const getConfigSelectedId = (userData: any): string | undefined => {
  const selected = userData?.configSelected;
  if (!selected) return undefined;
  if (typeof selected === 'string') return selected;
  return selected.id || selected._id || selected.config;
};

const normalizeTransactionForStore = (transaction: any) => {
  const created = new Date(transaction.createdAt || transaction.createdDate || Date.now());
  const createdDate = !Number.isNaN(created.getTime()) ? created.toISOString() : new Date().toISOString();
  const configId = transaction.config?.id || transaction.config?._id || transaction.config;

  return {
    ...transaction,
    id: transaction.id || transaction._id,
    invoiceNumber: transaction.invoiceNumber || transaction.transactionNumber,
    transactionNumber: transaction.transactionNumber,
    config: configId,
    configId,
    createdDate,
    dueDate: transaction.dueDate || createdDate,
    status: typeof transaction.status === 'string'
      ? { value: transaction.status, label: transaction.status }
      : transaction.status,
    items: Array.isArray(transaction.items) ? transaction.items : [],
  };
};

// -----------------------------------------------------------------------------
// Main hook
// -----------------------------------------------------------------------------

export function useWsHandlers(options: UseWsHandlersOptions = {}): UseWsHandlersReturn {
  const { onClientKeyChange } = options;

  const router = useRouter();
  const { user, updateCurrentUser } = useAuthContext();
  const { onRefreshRoles } = useGetUsers();
  const { onRefreshSupplies } = useSupplies();
  const { onUpdateProducts } = useProducts();
  const { onRefreshInvoices, onRemoveDraft, onUpdateInvoices } = useInvoices();
  const { onUpdateCustomer, onUpdateCustomers } = useCustomers();
  const { onSelectInvoice, activeStep } = useCheckout();

  // Use refs to avoid re-creating handlers on every render
  const handlersRef = useRef({
    router,
    user,
    updateCurrentUser,
    onRefreshRoles,
    onRefreshSupplies,
    onUpdateProducts,
    onRemoveDraft,
    onRefreshInvoices,
    onUpdateInvoices,
    onUpdateCustomer,
    onUpdateCustomers,
    onSelectInvoice,
    activeStep,
    onClientKeyChange,
  });

  // Update refs when dependencies change
  handlersRef.current = {
    router,
    user,
    updateCurrentUser,
    onRefreshRoles,
    onRefreshSupplies,
    onUpdateProducts,
    onRefreshInvoices,
    onUpdateInvoices,
    onRemoveDraft,
    onUpdateCustomer,
    onUpdateCustomers,
    onSelectInvoice,
    activeStep,
    onClientKeyChange,
  };

  // =========================================================================
  // VOUCHER HANDLERS (Phase 3)
  // Pattern: dispatch DOM events only (like handleSessionUpdated).
  // Redux updates and UI refreshes are handled by WsProvider listeners.
  // =========================================================================

  const handleVoucherCreated = useCallback((message: WsVoucherCreatedMessage) => {
    const { data } = message;

    // Dispatch a custom DOM event so any voucher-listening component can react
    window.dispatchEvent(
      new CustomEvent('ws:voucher-created', {
        detail: {
          voucher: {
            id: data.id,
            code: data.code,
            customerId: data.customerId,
            type: data.type || 'purchase',
            totalAmount: data.totalAmount,
            remainingAmount: data.remainingAmount,
            status: data.status,
            sourceTransactionId: data.sourceTransactionId,
            createdAt: new Date().toISOString(),
          },
          customerId: data.customerId,
        },
      }),
    );

    console.info('[WebSocket] Voucher created:', data.code, '| Customer:', data.customerId);
  }, []);

  const handleVoucherUpdated = useCallback((message: WsVoucherUpdatedMessage) => {
    const { data } = message;

    // Dispatch a custom DOM event
    window.dispatchEvent(
      new CustomEvent('ws:voucher-updated', {
        detail: data,
      }),
    );

    console.info('[WebSocket] Voucher updated:', data.id, '| Remaining:', data.remainingAmount);
  }, []);

  // =========================================================================
  // RETURN HANDLERS (Phase 3)
  // Pattern: dispatch DOM events only (like handleVoucherCreated).
  // Redux updates and UI refreshes are handled by WsProvider listeners.
  // =========================================================================

  const handleReturnCreated = useCallback((message: WsReturnCreatedMessage) => {
    const { data } = message;

    // Dispatch a custom DOM event so any return-listening component can react
    window.dispatchEvent(
      new CustomEvent('ws:return-created', {
        detail: {
          return: {
            id: data.id,
            transactionNumber: data.transactionNumber,
            returnAuthNumber: data.returnAuthNumber,
            sourceType: data.sourceType,
            customerId: data.customerId,
            customerName: data.customerName,
            totalAmount: data.totalAmount,
          },
          customerId: data.customerId,
          transactionNumber: data.transactionNumber,
          totalAmount: data.totalAmount,
          items: data.items,
          refunds: data.refunds,
        },
      }),
    );

    console.info(
      '[WebSocket] Return created:',
      data.transactionNumber,
      '| Type:',
      data.sourceType,
      '| Amount:',
      data.totalAmount,
    );
  }, []);

  const handleReturnUpdated = useCallback((message: WsReturnUpdatedMessage) => {
    const { data } = message;

    // Dispatch a custom DOM event
    window.dispatchEvent(
      new CustomEvent('ws:return-updated', {
        detail: {
          id: data.id,
          transactionNumber: data.transactionNumber,
          status: data.status,
        },
      }),
    );

    console.info('[WebSocket] Return updated:', data.transactionNumber, '| Status:', data.status);
  }, []);

  const handleNotificationCreated = useCallback((message: WsNotificationCreatedMessage) => {
    window.dispatchEvent(
      new CustomEvent('ws:notification-created', {
        detail: message.data,
      }),
    );
  }, []);

  // =========================================================================
  // EXISTING HANDLERS
  // =========================================================================

  const handleAuthResponse = useCallback((message: WsAuthResponseMessage) => {
    if (message.success && message.key) {
      window.localStorage.setItem('WS-CLIENT-KEY', message.key);
      handlersRef.current.onClientKeyChange?.(message.key);
      console.info('[WebSocket] Authentication successful, client key received');
    } else if (!message.success) {
      console.warn('[WebSocket] Authentication failed:', message.error);
      handlersRef.current.onClientKeyChange?.(null);
    }
  }, []);

  const handleUserEvent = useCallback((message: WsUserEventMessage) => {
    if (message.key) {
      window.localStorage.setItem('WS-CLIENT-KEY', message.key);
      handlersRef.current.onClientKeyChange?.(message.key);
    }
    console.info(`[WebSocket] Connection status: ${message.message}`);
  }, []);

  const handleTransactionCreated = useCallback((message: WsTransactionCreatedMessage) => {
    const { data } = message;

    // Remove draft using idempotencyKey first (most reliable), then fallback to transactionNumber and id
    onRemoveDraft(data.idempotencyKey || data.transactionNumber || data.id);
    // Mise à jour locale immédiate : éviter de recharger toute la liste des
    // transactions après chaque vente (source majeure de lenteur/refresh).
    handlersRef.current.onUpdateInvoices([normalizeTransactionForStore(data)]);
    // Handle return transactions
    if (data?.type === 'return') {
      handlersRef.current.router.push('/dashboard/order/invoices');
    }
    // Handle new invoice in checkout
    else if (handlersRef.current.activeStep === 1) {
      handlersRef.current.onSelectInvoice(data);
    }

    console.info('[WebSocket] Transaction created:', data);
  }, [onRemoveDraft]);

  const handleTransactionUpdated = useCallback((message: WsTransactionUpdatedMessage) => {
    const { data } = message;
    if (!data) return;

    handlersRef.current.onUpdateInvoices([normalizeTransactionForStore(data)]);

    // A final backend update (synced/failed) is authoritative. Once Mongo has
    // the sale, any local draft with the same stable identity can be removed.
    const draftIdentity = data.idempotencyKey || data.transactionNumber || data.id;
    if (draftIdentity) onRemoveDraft(draftIdentity);

    console.info('[WebSocket] Transaction updated:', data.transactionNumber || data.id, data.syncStatus);
  }, [onRemoveDraft]);

  const handleProductsFromErp = useCallback((message: WsProductsFromErpMessage) => {
    const { data, fieldsSearchable } = message;
    handlersRef.current.onUpdateProducts(data, fieldsSearchable);
    console.info('[WebSocket] Products updated from ERP:', data.length, 'items');
  }, []);

  const handleRolesUpdated = useCallback(() => {
    const currentUser = handlersRef.current.user;
    if (currentUser?.id) {
      handlersRef.current.onRefreshRoles(currentUser.id);
    }
    handlersRef.current.updateCurrentUser(undefined).then();
    console.info('[WebSocket] Roles updated for user:', currentUser?.id);
  }, []);

  const handleSupplyCreated = useCallback(() => {
    const currentUser = handlersRef.current.user;
    if (currentUser?.id) {
      handlersRef.current.onRefreshSupplies({ idUser: currentUser.id });
    }
    console.info('[WebSocket] Supply created');
  }, []);

  const handleCustomerCreated = useCallback((message: WsCustomerCreatedMessage) => {
    const { data } = message;
    if (data) {
      handlersRef.current.onUpdateCustomer(data);
      console.info('[WebSocket] Customer created/updated:', data.email || data.id);
    }
  }, []);

  const handleSessionUpdated = useCallback((message: WsSessionUpdatedMessage) => {
    const { data } = message;
    console.log('[useWsHandlers] SESSION_UPDATED received:', {
      sessionId: data?.id,
      currentAmount: data?.currentAmount,
      totalSales: data?.totalSales,
      summary: data?.summary,
      fullData: data,
    });

    // Dispatch a custom event to update the Redux store
    if (data && data.id) {
      window.dispatchEvent(
        new CustomEvent('ws:session-updated', {
          detail: data,
        }),
      );
    }

    console.info('[WebSocket] Session updated:', data.id, 'currentAmount:', data.currentAmount);
  }, []);

  // =========================================================================
  // HANDLERS POUR LE STREAMING DES DONNEES D'INITIALISATION
  // =========================================================================

  type InitDataEntity = keyof typeof initDataAccumulators;

  const handleInitDataStart = useCallback((message: WsIncomingMessage) => {
    const { entity, totalItems, totalChunks } = message as {
      entity: InitDataEntity;
      totalItems: number;
      totalChunks: number;
    };

    // Reinitialiser l'accumulateur pour cette entite
    initDataAccumulators[entity] = [];

    // Dispatcher un evenement pour l'UI (barre de progression)
    window.dispatchEvent(
      new CustomEvent('ws:init-data-start', {
        detail: { entity, totalItems, totalChunks },
      }),
    );

    console.info(
      `[WebSocket] Init data streaming started for ${entity}: ${totalItems} items in ${totalChunks} chunks`,
    );
  }, []);

  const handleInitDataChunk = useCallback((message: WsIncomingMessage) => {
    const { entity, chunkIndex, totalChunks, data, isLast } = message as {
      entity: InitDataEntity;
      chunkIndex: number;
      totalChunks: number;
      data: any[];
      isLast: boolean;
    };

    // Accumuler les donnees
    initDataAccumulators[entity].push(...data);

    // Dispatcher un evenement pour mettre a jour la progression
    window.dispatchEvent(
      new CustomEvent('ws:init-data-progress', {
        detail: {
          entity,
          chunkIndex,
          totalChunks,
          isLast,
          accumulatedCount: initDataAccumulators[entity].length,
        },
      }),
    );

    console.info(
      `[WebSocket] Received chunk ${chunkIndex + 1}/${totalChunks} for ${entity} (${data.length} items)`,
    );
  }, []);

  const handleInitDataComplete = useCallback((message: WsIncomingMessage) => {
    const { entity, totalItems } = message as { entity: InitDataEntity; totalItems: number };
    const accumulatedData = initDataAccumulators[entity];

    console.info(
      `[WebSocket] Init data streaming complete for ${entity}: ${accumulatedData.length} items received`,
    );

    // Mettre a jour le store Redux avec les donnees accumulees
    if (entity === 'products') {
      onUpdateProducts(accumulatedData, []);
    } else if (entity === 'customers') {
      handlersRef.current.onUpdateCustomers(accumulatedData);
    } else if (entity === 'transactions') {
      handlersRef.current.onUpdateInvoices(accumulatedData.map(normalizeTransactionForStore));
    }

    // Dispatcher un evenement pour finaliser
    window.dispatchEvent(
      new CustomEvent('ws:init-data-complete', {
        detail: { entity, totalItems, data: accumulatedData },
      }),
    );

    // Reinitialiser l'accumulateur
    initDataAccumulators[entity] = [];
  }, [onUpdateProducts]);

  // =========================================================================
  // HANDLER POUR LES DONNEES DIFFEREES (trop volumineuses pour WebSocket)
  // =========================================================================

  const handleInitDataDeferred = useCallback(
    async (message: WsInitDataDeferredMessage) => {
      const { entity, totalItems: count, reason } = message;

      console.info(
        `[WebSocket] Data deferred for ${entity}: ${count} items (${reason}). Loading via paginated API...`,
      );

      // Dispatcher un evenement pour informer l'UI
      window.dispatchEvent(
        new CustomEvent('ws:init-data-deferred', {
          detail: { entity, count, reason },
        }),
      );

      // Charger les donnees paginees en arriere-plan
      try {
        const { loadAllPaginatedProducts } = await import('src/api/server/init');
        const currentUser = handlersRef.current.user;

        if (!currentUser?.id || !currentUser?.configSelected?._id) {
          console.warn('[WebSocket] Cannot load paginated products: missing user or config');
          return;
        }

        const onProgress = (page: number, totalPages: number, itemsLoaded: number) => {
          console.info(
            `[INIT] Loading paginated products: page ${page}/${totalPages} (${itemsLoaded} items)`,
          );
          window.dispatchEvent(
            new CustomEvent('ws:init-data-progress', {
              detail: {
                entity: 'products',
                chunkIndex: page - 1,
                totalChunks: totalPages,
                isLast: page === totalPages,
                accumulatedCount: itemsLoaded,
              },
            }),
          );
        };

        const allProducts = await loadAllPaginatedProducts(
          currentUser.id,
          currentUser.configSelected._id,
          onProgress,
        );

        // Mettre a jour le store et le cache
        onUpdateProducts(allProducts, []);

        window.dispatchEvent(
          new CustomEvent('ws:init-data-complete', {
            detail: {
              entity: 'products',
              totalItems: allProducts.length,
              data: allProducts,
            },
          }),
        );

        console.info(
          `[WebSocket] Successfully loaded ${allProducts.length} products via pagination`,
        );
      } catch (error) {
        console.error('[WebSocket] Error loading paginated products:', error);
      }
    },
    [onUpdateProducts],
  );

  // =========================================================================
  // MAIN MESSAGE DISPATCHER
  // =========================================================================

  const handleMessage = useCallback(
    (message: WsIncomingMessage) => {
      if (!message?.type) {
        console.warn('[WebSocket] Received message without type:', message);
        return;
      }

      try {
        // Heartbeat messages are consumed by useWsConnection telemetry.
        if (message.type === 'pong') return;

        if (isAuthResponseMessage(message)) {
          handleAuthResponse(message);
        } else if (isUserEventMessage(message)) {
          handleUserEvent(message);
        } else if (isTransactionCreatedMessage(message)) {
          handleTransactionCreated(message);
        } else if (isTransactionUpdatedMessage(message)) {
          handleTransactionUpdated(message);
        } else if (isSessionUpdatedMessage(message)) {
          handleSessionUpdated(message);
        } else if (isVoucherCreatedMessage(message)) {
          handleVoucherCreated(message);
        } else if (isVoucherUpdatedMessage(message)) {
          handleVoucherUpdated(message);
        } else if (isReturnCreatedMessage(message)) {
          handleReturnCreated(message);
        } else if (isReturnUpdatedMessage(message)) {
          handleReturnUpdated(message);
        } else if (isProductsFromErpMessage(message)) {
          handleProductsFromErp(message);
        } else if (isRolesUpdatedMessage(message)) {
          handleRolesUpdated();
        } else if (isSupplyCreatedMessage(message)) {
          handleSupplyCreated();
        } else if (isCustomerCreatedMessage(message)) {
          handleCustomerCreated(message);
        } else if (isNotificationCreatedMessage(message)) {
          handleNotificationCreated(message);
        } else if (isInitDataStartMessage(message)) {
          handleInitDataStart(message);
        } else if (isInitDataChunkMessage(message)) {
          handleInitDataChunk(message);
        } else if (isInitDataCompleteMessage(message)) {
          handleInitDataComplete(message);
        } else if (isInitDataDeferredMessage(message)) {
          // Trop de produits pour le streaming — charger via API paginee
          handleInitDataDeferred(message).then();
        } else {
          console.log('[WebSocket] Unhandled message type:', message.type);
        }
      } catch (err) {
        console.error('[WebSocket] Error handling message:', err, message);
      }
    },
    [
      handleAuthResponse,
      handleUserEvent,
      handleTransactionCreated,
      handleTransactionUpdated,
      handleSessionUpdated,
      handleVoucherCreated,
      handleVoucherUpdated,
      handleReturnCreated,
      handleReturnUpdated,
      handleProductsFromErp,
      handleRolesUpdated,
      handleSupplyCreated,
      handleCustomerCreated,
      handleNotificationCreated,
      handleInitDataStart,
      handleInitDataChunk,
      handleInitDataComplete,
      handleInitDataDeferred,
    ],
  );

  const handleAuth = useCallback((userData: any, key: string | null, selectedConfigId?: string) => {
    const configSelected = selectedConfigId || getConfigSelectedId(userData);
    const message = {
      type: 'auth' as const,
      user: userData,
      key,
      ...(configSelected ? { configSelected } : {}),
    };
    console.info('[WebSocket] Sending auth message for user:', userData?.id, 'config:', configSelected);
    return message;
  }, []);

  const handleLogout = useCallback((key: string | null) => {
    const message = {
      type: 'auth' as const,
      user: null as any,
      key,
    };
    console.info('[WebSocket] Sending logout message');
    return message;
  }, []);

  return {
    handleMessage,
    handleAuth,
    handleLogout,
  };
}
